import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnDestroy, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subscription, forkJoin, of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

// Import Angular Material modules
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { SpricedFilterDialogComponent } from '../../global-components/spriced-filter-dialog/spriced-filter-dialog.component';
import { SpricedSavedFiltersDialogComponent } from '../../global-components/spriced-saved-filters-dialog/spriced-saved-filters-dialog.component';

@Component({
  selector: 'app-entity-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './entity-management.component.html',
  styleUrls: ['./entity-management.component.scss']
})
export class EntityManagementComponent implements OnInit, OnDestroy {
  @Input() data: any;
  @Output() recordSelected = new EventEmitter<any>();
  @Output() headerAction = new EventEmitter<any>();

  get componentColourStyles(): Record<string, string> {
    const palette = Array.isArray(this.data?.themeOverrides?.chart?.palette)
      ? this.data.themeOverrides.chart.palette
      : [];
    const primaryDefault = palette[0];
    const primaryDark = palette[1] || palette[0];

    const styles: Record<string, string> = {
      '--entity-management-primary-text': '#ffffff',
    };

    if (primaryDefault) {
      styles['--entity-management-primary-default'] = primaryDefault;
    }

    if (primaryDark) {
      styles['--entity-management-primary-dark'] = primaryDark;
    }

    return styles;
  }

  get containerHeight(): string {
    const configured = this.data?.height || this.data?.chartConfig?.height;
    if (typeof configured === 'number') {
      return `${configured}px`;
    }
    if (typeof configured === 'string' && configured.trim()) {
      const normalized = configured.trim().toLowerCase();
      if (normalized.endsWith('vh') || normalized.endsWith('vw') || normalized.endsWith('%')) {
        return '100%';
      }
      return configured;
    }
    return '100%';
  }

  records: any[] = [];
  selectedRecord: any = null;
  loading = true;
  currentFilters: any = {};
  selection = new Set<any>();
  allSelected = false;
  headerTitle: string = '';
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  currentPage = 1;
  openHeaderDropdownKey: string | null = null;
  protected readonly dropdownSearchControls: Record<string, FormControl<string | null>> = {};

  @ViewChild('settingsDialog') settingsDialog?: TemplateRef<any>;
  @ViewChild('cellActionDialog') cellActionDialog?: TemplateRef<any>;

  // Column resizing properties
  columnWidths: (number | undefined)[] = [];
  frozenColumnsCount: number = 0;
  displayFormat: string = 'code_name';
  showSystemAttribute: boolean = true;

  localPageSize: number = 200;
  localFrozenCols: number = 0;
  localDisplayFormat: string = 'code_name';
  localShowSystemAttribute: boolean = true;
  localDefaults: string = 'restore';

  dialogRef?: any;
  cellActionDialogRef?: any;
  activeCellDialogConfig: any = null;
  activeCellDialogColumns: any[] = [];
  activeCellDialogRows: any[] = [];
  activeCellDialogTitle = '';
  activeCellDialogPageSize = 10;
  activeCellDialogAnnotation = '';



  private resizingColumnIndex: number = -1;
  private startX: number = 0;
  private startWidth: number = 0;
  private mouseMoveListener!: () => void;
  private mouseUpListener!: () => void;

  private filterSub?: Subscription;
  private refreshSub?: Subscription;
  private reflowSub?: Subscription;
  private dropdownKeys: string[] = [];
  private pendingDialogRequestData: any = null;
  private appliedDialogFilterPayload: any = null;
  private appliedDialogFilterSummaryLines: string[] = [];
  protected hasDialogFilterApplied = false;
  private documentClickListener?: () => void;
  private cellActionCache = new WeakMap<object, any | null>();
  private cellActionCacheEntity: any = Symbol('uninitialized');
  private cellActionCacheSource: any[] | null = null;

  private getExcludedColumns(): string[] {
    const excluded = this.data?.excludeColumns || this.data?.hiddenColumns || this.data?.excludeFields;
    return Array.isArray(excluded) ? excluded.map((item: any) => String(item).trim()).filter(Boolean) : [];
  }

  protected formatColumnLabel(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    return normalizedValue
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase());
  }

  formatCellValue(value: any, column?: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (this.shouldRenderDateOnly(column, value)) {
      return this.formatDateOnlyValue(value);
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    const data = value?.data ?? value;
    const code = data?.code ?? data?.id ?? value?.code ?? value?.id;
    const name = data?.name ?? data?.display_name ?? data?.displayName ?? value?.name ?? value?.display_name ?? value?.displayName;

    if (code !== undefined && name !== undefined && code !== null && name !== null) {
      if (this.displayFormat === 'code') {
        return String(code);
      }
      if (this.displayFormat === 'name') {
        return String(name);
      }
      return `${code}{${name}}`;
    }
    if (name !== undefined && name !== null) {
      return String(name);
    }
    if (code !== undefined && code !== null) {
      return String(code);
    }
    return JSON.stringify(value);
  }

  private shouldRenderDateOnly(column: any, value: any): boolean {
    if (!this.looksLikeDateValue(value)) {
      return false;
    }

    const normalizedSource = `${this.normalizeDateFieldToken(column?.key)}${this.normalizeDateFieldToken(column?.label)}`;
    return !!normalizedSource && normalizedSource.includes('effective') && normalizedSource.includes('date');
  }

  private looksLikeDateValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return (
        /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(trimmed) ||
        /^\d{2}\/\d{2}\/\d{4}(?:\s.*)?$/.test(trimmed) ||
        !isNaN(Date.parse(trimmed))
      );
    }

    return false;
  }

  private normalizeDateFieldToken(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  private formatDateOnlyValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (value instanceof Date) {
      if (isNaN(value.getTime())) {
        return '';
      }

      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      const isoDateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoDateMatch?.[1]) {
        return isoDateMatch[1];
      }

      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        const year = parsed.getFullYear();
        const month = String(parsed.getMonth() + 1).padStart(2, '0');
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }

    return String(value);
  }

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private renderer: Renderer2,
    protected dialog: MatDialog,
    private responsiveLayoutService: ResponsiveLayoutService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (this.data?.header?.title) {
      this.headerTitle = this.data.header.title;
    }

    if (this.data?.header?.dropdowns) {
      this.dropdownKeys = this.data.header.dropdowns.map((d: any) => d.key);
      this.data.header.dropdowns.forEach((dropdown: any) => {
        dropdown.optionsList = Array.isArray(dropdown.options) ? dropdown.options : [];
        this.sortDropdownOptions(dropdown);
        if (dropdown.defaultValue) {
          this.currentFilters[dropdown.key] = dropdown.defaultValue;
          if (dropdown.key === this.getEntityFilterKey()) {
            this.globalFilterService.updateFilterSelection(
              dropdown.key,
              [dropdown.defaultValue],
              dropdown?.isQueryParam === true,
              this.data?.widgetId,
            );
          }
        }
        this.syncDropdownSelectionFromOptions(dropdown.key);
        this.syncSelectedEntityLabel(dropdown.key);
      });
    }

    const initObservables: { [key: string]: Observable<any> } = {
      mainData: this.loadRecords()
    };

    if (this.data?.header?.dropdowns) {
      this.data.header.dropdowns.forEach((dropdown: any, index: number) => {
        if (dropdown.options?.apiUrl) {
          initObservables[`dropdown${index}`] = this.fetchDropdownOptions(dropdown);
        }
      });
    }

    forkJoin(initObservables).subscribe({
      error: (err) => {
        console.error("Initialization failed:", err);
        this.loading = false;
      }
    });

    this.setupFilterListener();
    this.setupRefreshListener();

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.cdr.detectChanges();
    });

    this.documentClickListener = this.renderer.listen('document', 'click', () => {
      if (this.openHeaderDropdownKey !== null) {
        this.openHeaderDropdownKey = null;
        this.cdr.detectChanges();
      }
    });
  }

  fetchDropdownOptions(dropdown: any): Observable<any> {
    const optionsConfig = dropdown?.options || {};
    const { apiUrl, key, headers } = optionsConfig;
    const configuredMethod = (optionsConfig?.method || 'get').toLowerCase();
    const method = this.isLocalAssetUrl(apiUrl) ? 'get' : configuredMethod;
    const excludePayloadParams = Array.isArray(optionsConfig?.excludePayloadParams)
      ? optionsConfig.excludePayloadParams
      : [];
    const filteredCurrentFilters = Object.keys(this.currentFilters || {}).reduce((acc: any, filterKey: string) => {
      if (!excludePayloadParams.includes(filterKey)) {
        acc[filterKey] = this.currentFilters[filterKey];
      }
      return acc;
    }, {});

    let payload: any = {};
    if (method === 'post') {
      if (optionsConfig?.skipDefaultPayload) {
        payload = JSON.parse(JSON.stringify(optionsConfig?.customPayload || {}));
        payload.requestData = {
          ...(payload.requestData || {}),
          ...filteredCurrentFilters
        };
      } else {
        payload = {
          requestData: {
            ...(optionsConfig?.defaultRequestData || {}),
            ...filteredCurrentFilters,
            ...(optionsConfig?.customPayload || {}),
          },
          responseList: [],
          entity: optionsConfig?.entity || '',
          workflowId: optionsConfig?.workflowId || '',
          endpoints: [
            {
              functionName: optionsConfig?.functionName || '',
              version: optionsConfig?.version || '1.0',
            },
          ],
          communicationType: 'rest',
          version: optionsConfig?.version || '1.0',
        };
      }
    }

    return this.configService.getDropdownOptions(apiUrl, payload, method, headers).pipe(
      map((res: any) => {
        const optionsArray = this.extractDropdownOptionsFromResponse(res, key || dropdown?.key);
        dropdown.optionsList = Array.isArray(optionsArray) ? optionsArray : [];
        this.sortDropdownOptions(dropdown);
        this.syncDropdownSelectionFromOptions(dropdown.key);
        this.syncSelectedEntityLabel(dropdown.key);
        return dropdown;
      })
    );
  }

  getDropdownOptionLabel(dropdown: any, option: any): string {
    if (option === null || option === undefined) {
      return '';
    }
    if (typeof option !== 'object') {
      return String(option);
    }

    const labelKey = this.getDropdownOptionKey(dropdown, 'label');
    return String(
      option[labelKey] ??
      option.display_name ??
      option.displayName ??
      option.label ??
      option.name ??
      option.value ??
      ''
    );
  }

  getDropdownOptionValue(dropdown: any, option: any): any {
    if (option === null || option === undefined || typeof option !== 'object') {
      return option;
    }

    const valueKey = this.getDropdownOptionKey(dropdown, 'value');
    return (
      option[valueKey] ??
      option.display_name ??
      option.displayName ??
      option.value ??
      option.name ??
      option
    );
  }

  getDropdownSelectedLabel(dropdown: any, selectedValue: any): string {
    const selectedOption = (dropdown?.optionsList || []).find((option: any) =>
      this.getDropdownOptionValue(dropdown, option) === selectedValue
    );

    if (selectedOption) {
      return this.getDropdownOptionLabel(dropdown, selectedOption);
    }

    return this.getDropdownOptionLabel(dropdown, selectedValue);
  }

  getDropdownSearchControl(dropdownKey: any): FormControl<string | null> {
    const normalizedKey = this.normalizeDropdownSearchKey(dropdownKey);
    if (!this.dropdownSearchControls[normalizedKey]) {
      this.dropdownSearchControls[normalizedKey] = new FormControl('');
    }

    return this.dropdownSearchControls[normalizedKey];
  }

  getFilteredDropdownOptions(dropdown: any): any[] {
    const optionsList = Array.isArray(dropdown?.optionsList) ? dropdown.optionsList : [];
    const searchTerm = String(this.getDropdownSearchControl(dropdown?.key).value || '')
      .trim()
      .toLowerCase();

    if (!searchTerm) {
      return optionsList;
    }

    return optionsList.filter((option: any) => {
      const optionLabel = this.getDropdownOptionLabel(dropdown, option).toLowerCase();
      const optionValue = this.getDropdownSearchableValue(dropdown, option).toLowerCase();

      return optionLabel.includes(searchTerm) || optionValue.includes(searchTerm);
    });
  }

  onDropdownOpenedChange(dropdownKey: any, opened: boolean): void {
    if (opened) {
      this.getDropdownSearchControl(dropdownKey);
      return;
    }

    this.getDropdownSearchControl(dropdownKey).setValue('');
  }

  toggleHeaderDropdown(dropdownKey: any, event?: Event): void {
    event?.stopPropagation();
    const normalizedKey = this.normalizeDropdownSearchKey(dropdownKey);
    this.openHeaderDropdownKey = this.openHeaderDropdownKey === normalizedKey ? null : normalizedKey;

    if (this.openHeaderDropdownKey) {
      this.getDropdownSearchControl(dropdownKey).setValue('');
      setTimeout(() => {
        const searchInput = document.querySelector<HTMLInputElement>('.custom-dropdown-search');
        searchInput?.focus();
      }, 0);
    }
  }

  isHeaderDropdownOpen(dropdownKey: any): boolean {
    return this.openHeaderDropdownKey === this.normalizeDropdownSearchKey(dropdownKey);
  }

  setDropdownSearchTerm(dropdownKey: any, searchTerm: string): void {
    this.getDropdownSearchControl(dropdownKey).setValue(searchTerm);
  }

  isHeaderDropdownOptionSelected(dropdown: any, option: any): boolean {
    return this.getDropdownOptionValue(dropdown, option) === this.currentFilters?.[dropdown?.key];
  }

  selectHeaderDropdownOption(dropdown: any, option: any): void {
    this.openHeaderDropdownKey = null;
    this.getDropdownSearchControl(dropdown?.key).setValue('');
    this.onDropdownSelectionChange(dropdown?.key, this.getDropdownOptionValue(dropdown, option));
  }

  private normalizeDropdownSearchKey(dropdownKey: any): string {
    return dropdownKey === null || dropdownKey === undefined ? '__default__' : String(dropdownKey);
  }

  private getDropdownSearchableValue(dropdown: any, option: any): string {
    const optionValue = this.getDropdownOptionValue(dropdown, option);
    if (optionValue === null || optionValue === undefined) {
      return '';
    }

    if (typeof optionValue !== 'object') {
      return String(optionValue);
    }

    return JSON.stringify(optionValue);
  }

  private getDropdownOptionKey(dropdown: any, keyType: 'label' | 'value'): string {
    const optionsConfig = dropdown?.options || {};
    const explicitKey =
      dropdown?.[`${keyType}Key`] ??
      dropdown?.[`option${this.capitalize(keyType)}Key`] ??
      optionsConfig?.[`${keyType}Key`] ??
      optionsConfig?.[`option${this.capitalize(keyType)}Key`];

    if (explicitKey) {
      return explicitKey;
    }

    return keyType;
  }

  private sortDropdownOptions(dropdown: any): void {
    if (!dropdown || dropdown?.key !== this.getEntityFilterKey() || !Array.isArray(dropdown.optionsList)) {
      return;
    }

    dropdown.optionsList = [...dropdown.optionsList].sort((a: any, b: any) => {
      const labelA = String(this.getDropdownOptionLabel(dropdown, a) || '');
      const labelB = String(this.getDropdownOptionLabel(dropdown, b) || '');

      const labelAStartsWithDigit = /^\d/.test(labelA.trim());
      const labelBStartsWithDigit = /^\d/.test(labelB.trim());

      if (labelAStartsWithDigit !== labelBStartsWithDigit) {
        return labelAStartsWithDigit ? -1 : 1;
      }

      return labelA.localeCompare(labelB, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  protected shouldUpdateHeaderTitle(dropdown: any): boolean {
    if (!dropdown) {
      return false;
    }

    if (dropdown?.updatesHeaderTitle === false || dropdown?.updateHeaderTitle === false) {
      return false;
    }

    return dropdown?.updatesHeaderTitle === true ||
      dropdown?.updateHeaderTitle === true ||
      dropdown?.key === this.getEntityFilterKey();
  }

  private extractDropdownOptionsFromResponse(response: any, responseKey?: string): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    const candidates = [
      responseKey ? response?.[responseKey] : null,
      response?.responseList?.[0]?.[responseKey || ''],
      response?.responseList?.[0]?.data?.[0]?.[responseKey || ''],
      response?.responseList?.[0]?.data?.[responseKey || ''],
      response?.responseList?.[0]?.data?.[0]?.data,
      response?.responseList?.[0]?.data,
      response?.data?.[0]?.data,
      response?.data,
      response?.options,
    ].filter((candidate) => candidate !== null && candidate !== undefined);

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private syncDropdownSelectionFromOptions(dropdownKey: string): void {
    if (!this.data?.header?.dropdowns || !dropdownKey) {
      return;
    }

    const dropdown = this.data.header.dropdowns.find((item: any) => item?.key === dropdownKey);
    if (!dropdown) {
      return;
    }

    const normalizedValue = this.normalizeDropdownSelection(dropdown, this.currentFilters?.[dropdownKey]);
    if (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== this.currentFilters?.[dropdownKey]) {
      this.currentFilters[dropdownKey] = normalizedValue;
      if (dropdownKey === this.getEntityFilterKey()) {
        this.globalFilterService.updateFilterSelection(
          dropdownKey,
          [normalizedValue],
          dropdown?.isQueryParam === true,
          this.data?.widgetId,
        );
      }
    }

    this.syncHeaderTitleFromDropdown(dropdownKey);
    this.syncSelectedEntityLabel(dropdownKey);
  }

  protected syncSelectedEntityLabel(dropdownKey: string): void {
    if (dropdownKey !== this.getEntityFilterKey() || !this.data?.header?.dropdowns) {
      return;
    }

    const dropdown = this.data.header.dropdowns.find((item: any) => item?.key === dropdownKey);
    if (!dropdown) {
      return;
    }

    const selectedValue = this.currentFilters?.[dropdownKey];
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return;
    }

    const selectedLabel = this.getDropdownSelectedLabel(dropdown, selectedValue);
    if (!selectedLabel) {
      return;
    }

    this.globalFilterService.updateFilterSelection(
      this.getSelectedEntityLabelFilterKey(),
      [selectedLabel],
      dropdown?.isQueryParam === true,
      this.data?.widgetId,
    );
  }

  protected syncHeaderTitleFromDropdown(dropdownKey: string): void {
    if (!this.data?.header?.dropdowns || !dropdownKey) {
      return;
    }

    const dropdown = this.data.header.dropdowns.find((item: any) => item?.key === dropdownKey);
    if (!this.shouldUpdateHeaderTitle(dropdown)) {
      return;
    }

    const selectedValue = this.currentFilters?.[dropdownKey];
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return;
    }

    this.headerTitle = this.getDropdownSelectedLabel(dropdown, selectedValue);
  }

  private normalizeDropdownSelection(dropdown: any, selectedValue: any): any {
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return selectedValue;
    }

    if (typeof selectedValue === 'object') {
      return this.getDropdownOptionValue(dropdown, selectedValue);
    }

    const normalizedSelectedValue = String(selectedValue).trim().toLowerCase();
    const selectedOption = (dropdown?.optionsList || []).find((option: any) => {
      const optionValue = this.getDropdownOptionValue(dropdown, option);
      const optionLabel = this.getDropdownOptionLabel(dropdown, option);

      return (
        String(optionValue ?? '').trim().toLowerCase() === normalizedSelectedValue ||
        String(optionLabel ?? '').trim().toLowerCase() === normalizedSelectedValue
      );
    });

    return selectedOption ? this.getDropdownOptionValue(dropdown, selectedOption) : selectedValue;
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private isLocalAssetUrl(url: string | undefined): boolean {
    return !!url && (url.startsWith('assets') || url.startsWith('/assets'));
  }

  private getEntityFilterKey(): string {
    return String(this.data?.entityFilterKey || this.data?.entityKey || 'entity');
  }

  private getSelectedEntityLabelFilterKey(): string {
    return String(this.data?.selectedEntityLabelFilterKey || this.data?.entityLabelFilterKey || 'selected_entity_label');
  }

  private getRequestFilters(): any {
    const excludePayloadParams = Array.isArray(this.data?.excludePayloadParams)
      ? this.data.excludePayloadParams
      : [];

    return Object.keys(this.currentFilters || {}).reduce((acc: any, filterKey: string) => {
      if (!excludePayloadParams.includes(filterKey)) {
        acc[filterKey] = this.currentFilters[filterKey];
      }
      return acc;
    }, {});
  }

  setupFilterListener(): void {
    this.filterSub = this.globalFilterService.filter$.subscribe((filters) => {
      if (!filters || !filters.widgetId || filters.widgetId === this.data.widgetId) {
        return; // Ignore updates from itself or those without a widgetId
      }
      const newFilters = filters.requestParams || filters.queryParams || {};
      this.currentFilters = { ...this.currentFilters, ...newFilters };
      Object.keys(newFilters || {}).forEach((filterKey) => this.syncDropdownSelectionFromOptions(filterKey));
      this.loadRecords().subscribe();
    });
  }

  setupRefreshListener(): void {
    this.refreshSub = this.globalFilterService.widgetRefresh$.subscribe((widgetId) => {
      if (widgetId === this.data.widgetId) {
        // Keep the applied dialog filter active across refreshes.
        if (this.hasDialogFilterApplied && this.appliedDialogFilterPayload) {
          this.pendingDialogRequestData = this.clonePayload(this.appliedDialogFilterPayload);
        }
        this.loadRecords().subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.documentClickListener?.();
    this.stopResizing();
  }

  loadRecords(): Observable<any[]> {
    this.loading = true;
    if (this.data?.widgetId) {
      this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
    }
    const selectedRecordId = this.selectedRecord ? this.selectedRecord.id : null;

    if (Array.isArray(this.data)) {
      this.records = this.data;
      this.resetPagination();
      if (this.records.length > 0) {
        this.selectRecord(this.records[0]);
      }
      this.loading = false;
      return of(this.records);
    }

    if (!this.data?.apiUrl) {
      this.records = [];
      this.resetPagination();
      this.loading = false;
      if (this.data?.widgetId) {
        this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
      }
      return of([]);
    }

    const method = this.data?.method?.toLowerCase() || 'post';
    let filtersToSend: any = {};
    const hasDialogPayload = !!this.pendingDialogRequestData;
    const currentFiltersForRequest = hasDialogPayload
      ? { ...this.getRequestFilters() }
      : this.getRequestFilters();
    const requestDataOverride = hasDialogPayload ? this.pendingDialogRequestData : null;
    this.pendingDialogRequestData = null;

    if (method === 'get') {
      if (this.data.queryParamsKeys && Array.isArray(this.data.queryParamsKeys)) {
        this.data.queryParamsKeys.forEach((key: string) => {
          if (currentFiltersForRequest[key]) {
            filtersToSend[key] = currentFiltersForRequest[key];
          }
        });
      } else {
        filtersToSend = { ...currentFiltersForRequest };
      }
      filtersToSend = { queryParams: filtersToSend };
    } else {
      filtersToSend = requestDataOverride
        ? { requestParams: {} }
        : { requestParams: { ...currentFiltersForRequest } };
    }

    const requestConfig = requestDataOverride
      ? {
        ...this.data,
        rawPayload: requestDataOverride,
        customPayload: requestDataOverride,
        skipDefaultPayload: true,
      }
      : this.data;

    return this.configService.getApi(this.data.apiUrl, filtersToSend, requestConfig, method).pipe(
      map((res: any) => {
        const key = this.data?.key || this.data?.responseKey || null;
        const recordsResult = this.extractRecordsFromResponse(res, key);

        this.records = recordsResult;
        this.resetPagination();

        if (!this.data.columns && this.records.length > 0) {
          const firstRecord = this.records[0];
          const excludedColumns = new Set(this.getExcludedColumns());
          const newColumns = Object.keys(firstRecord)
            .filter(key => key !== 'id' && !excludedColumns.has(key))
            .map(key => ({
              key: key,
              label: this.formatColumnLabel(key),
              sortable: true
            }));
          this.data.columns = newColumns;

          // Initialize columnWidths with default values
          this.columnWidths = [32]; // Status column
          newColumns.forEach(() => this.columnWidths.push(150)); // Default width for data columns
        }

        if (this.records.length > 0) {
          let recordToSelect = this.records[0]; // Default to first record
          if (selectedRecordId) {
            const reselectedRecord = this.records.find(r => r.id === selectedRecordId);
            if (reselectedRecord) {
              recordToSelect = reselectedRecord;
            }
          }
          this.selectRecord(recordToSelect);
        } else {
          this.selectRecord(null); // Clear selection if no records
        }

        this.loading = false;
        if (this.data?.widgetId) {
          this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
        }
        return this.records;
      }),
      catchError((error) => {
        console.error('Error loading records:', error);
        this.loading = false;
        this.records = [];
        this.resetPagination();
        if (this.data?.widgetId) {
          this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
        }
        return of([]); // Return an empty observable to complete the stream
      })
    );
  }

  protected extractRecordsFromResponse(response: any, responseKey?: string | null): any[] {
    const keyedValue = responseKey ? response?.[responseKey] : null;
    const candidates = [
      keyedValue,
      keyedValue?.data,
      response?.data,
      responseKey ? response?.data?.[responseKey] : null,
      responseKey ? response?.responseList?.[0]?.[responseKey] : null,
      responseKey ? response?.responseList?.[0]?.data?.[0]?.[responseKey] : null,
      responseKey ? response?.responseList?.[0]?.data?.[responseKey] : null,
      response?.responseList?.[0]?.data?.[0]?.data,
      response?.responseList?.[0]?.data,
      Array.isArray(response) ? response : null,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return this.unwrapRecordDataRows(candidate);
      }
    }

    return [];
  }

  protected unwrapRecordDataRows(records: any[]): any[] {
    return records.map((record) => {
      if (
        record &&
        typeof record === 'object' &&
        record.data &&
        typeof record.data === 'object' &&
        !Array.isArray(record.data) &&
        Object.keys(record).length === 1
      ) {
        return record.data;
      }

      return record;
    });
  }

  onResizeStart(event: MouseEvent, index: number): void {
    event.preventDefault();
    this.resizingColumnIndex = index;
    this.startX = event.clientX;
    const th = (event.target as HTMLElement).parentElement;
    this.startWidth = th?.offsetWidth || 0;

    this.mouseMoveListener = this.renderer.listen('document', 'mousemove', this.onResizing.bind(this));
    this.mouseUpListener = this.renderer.listen('document', 'mouseup', this.stopResizing.bind(this));
  }

  onResizing(event: MouseEvent): void {
    if (this.resizingColumnIndex > -1) {
      const deltaX = event.clientX - this.startX;
      const newWidth = this.startWidth + deltaX;
      if (newWidth > 50) { // Minimum column width
        this.columnWidths[this.resizingColumnIndex] = newWidth;
      }
    }
  }

  stopResizing(): void {
    this.resizingColumnIndex = -1;
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
    }
    if (this.mouseUpListener) {
      this.mouseUpListener();
    }
  }

    sortBy(columnKey: string) {
      if (this.sortColumn === columnKey) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = columnKey;
        this.sortDirection = 'asc';
      }

      const column = this.data?.columns?.find((item: any) => item?.key === columnKey);
      const direction = this.sortDirection === 'asc' ? 1 : -1;

      this.records.sort((a, b) =>
        this.compareRecordValues(a?.[columnKey], b?.[columnKey], column) * direction
      );
    }

    private compareRecordValues(leftValue: any, rightValue: any, column?: any): number {
      const normalizedLeft = this.getSortableCellValue(leftValue, column);
      const normalizedRight = this.getSortableCellValue(rightValue, column);

      if (normalizedLeft === normalizedRight) {
        return 0;
      }

      if (normalizedLeft === '') {
        return -1;
      }

      if (normalizedRight === '') {
        return 1;
      }

      const numericLeft = Number(normalizedLeft);
      const numericRight = Number(normalizedRight);
      const bothNumeric = normalizedLeft.trim() !== '' && normalizedRight.trim() !== '' && !Number.isNaN(numericLeft) && !Number.isNaN(numericRight);
      if (bothNumeric) {
        return numericLeft - numericRight;
      }

      const dateLeft = Date.parse(normalizedLeft);
      const dateRight = Date.parse(normalizedRight);
      const bothDates = !Number.isNaN(dateLeft) && !Number.isNaN(dateRight);
      if (bothDates) {
        return dateLeft - dateRight;
      }

      return normalizedLeft.localeCompare(normalizedRight, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }

    private getSortableCellValue(value: any, column?: any): string {
      return this.formatCellValue(value, column).trim();
    }

  getSortIcon(columnKey: string): string {
    if (this.sortColumn !== columnKey) {
      return 'unfold_more';
    }
    if (this.sortDirection === 'asc') {
      return 'arrow_upward';
    }
    return 'arrow_downward';
  }

  get paginationEnabled(): boolean {
    return this.data?.pagination?.enabled === true;
  }

  get pageSize(): number {
    const configuredPageSize = Number(this.data?.pagination?.pageSize);
    return Number.isFinite(configuredPageSize) && configuredPageSize > 0 ? configuredPageSize : 50;
  }

  get totalPages(): number {
    if (!this.paginationEnabled) {
      return 1;
    }

    return Math.max(1, Math.ceil((this.records?.length || 0) / this.pageSize));
  }

  get pagedRecords(): any[] {
    if (!this.paginationEnabled) {
      return this.records || [];
    }

    this.clampCurrentPage();
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return (this.records || []).slice(startIndex, startIndex + this.pageSize);
  }

  get visiblePageNumbers(): number[] {
    const maxVisiblePages = 5;
    const totalPages = this.totalPages;
    const visibleCount = Math.min(maxVisiblePages, totalPages);
    const halfWindow = Math.floor(visibleCount / 2);
    let startPage = Math.max(1, this.currentPage - halfWindow);
    const endPage = Math.min(totalPages, startPage + visibleCount - 1);
    startPage = Math.max(1, endPage - visibleCount + 1);

    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }

  goToPage(page: number): void {
    if (!this.paginationEnabled) {
      return;
    }

    this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  protected resetPagination(): void {
    this.currentPage = 1;
  }

  protected clampCurrentPage(): void {
    this.currentPage = Math.min(Math.max(this.currentPage || 1, 1), this.totalPages);
  }

  handleHeaderAction(actionId: string, action?: any): void {
    this.headerAction.emit({
      actionId,
      action,
      selectedRecord: this.selectedRecord,
      selectedRecords: Array.from(this.selection),
      records: this.records
    });

    switch (actionId) {
      case 'refresh':
        // Re-apply the currently applied dialog filter (if any) so refreshing
        // keeps the filter active instead of reloading the unfiltered records.
        if (this.hasDialogFilterApplied && this.appliedDialogFilterPayload) {
          this.pendingDialogRequestData = this.clonePayload(this.appliedDialogFilterPayload);
        }
        this.loadRecords().subscribe();
        break;
      case 'filter':
        this.openFilterDialog(action);
        break;
      case 'clear_filter':
        this.clearFilterAction();
        break;
      case 'add':
        this.addRecord();
        break;
      case 'delete':
        this.deleteSelectedRecords();
        break;
      case 'hide':
        this.hideSelectedRecords();
        break;
      case 'download':
        this.downloadRecords(action);
        break;
      case 'menu':
        this.openSavedFiltersDialog(action);
        break;
      case 'history':
        this.publishHeaderActionState(actionId);
        break;
      case 'table_view':
        this.openSavedFiltersDialog(action);
        break;
      case 'settings':
        this.openSettingsDialog();
        break;
      default:
        this.publishHeaderActionState(actionId);
        break;
    }
  }

  isHeaderActionDisabled(action: any): boolean {
    if (action?.disabled === true) {
      return true;
    }

    const actionId = action?.actionId;
    return (actionId === 'delete' || actionId === 'hide') && !this.selectedRecord && this.selection.size === 0;
  }

  openSettingsDialog(): void {
    this.localPageSize = this.data?.pagination?.pageSize || 200;
    this.localFrozenCols = this.frozenColumnsCount;
    this.localDisplayFormat = this.displayFormat;
    this.localShowSystemAttribute = this.showSystemAttribute;

    if (this.settingsDialog && this.dialog) {
      this.dialogRef = this.dialog.open(this.settingsDialog, {
        width: '320px',
        panelClass: 'custom-download-dialog-panel'
      });
    }
  }

  closeSettingsDialog(save: boolean): void {
    if (save) {
      if (!this.data) {
        this.data = {};
      }
      if (!this.data.pagination) {
        this.data.pagination = {};
      }

      this.data.pagination.pageSize = this.localPageSize;
      this.frozenColumnsCount = this.localFrozenCols;
      this.displayFormat = this.localDisplayFormat;
      this.showSystemAttribute = this.localShowSystemAttribute;

      this.clampCurrentPage();

      this.data.columns = undefined;
      this.loadRecords().subscribe();
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  getFrozenColumnLeft(index: number): number {
    if (index >= this.frozenColumnsCount) {
      return 0;
    }
    let left = this.columnWidths[0] || 40; // checkbox column width
    for (let i = 0; i < index; i++) {
      left += (this.columnWidths[i + 1] || 150);
    }
    return left;
  }

  private addRecord(): void {
    const newRecord: any = {};
    if (this.data?.columns) {
      this.data.columns.forEach((col: any) => {
        newRecord[col.key] = '';
      });
    }
    newRecord.id = null; // Special marker for a new record
    this.selectRecord(newRecord);
  }

  protected deleteSelectedRecords(): void {
    const selectedRecords = this.getSelectedRecordsForAction();
    if (selectedRecords.length === 0) {
      return;
    }

    const selectedSet = new Set(selectedRecords);
    this.records = this.records.filter((record) => !selectedSet.has(record));
    this.clampCurrentPage();
    this.selection.clear();
    this.allSelected = false;
    this.selectRecord(this.records[0] || null);
    this.publishHeaderActionState('delete', selectedRecords);
  }

  private hideSelectedRecords(): void {
    const selectedRecords = this.getSelectedRecordsForAction();
    if (selectedRecords.length === 0) {
      return;
    }

    const selectedSet = new Set(selectedRecords);
    this.records = this.records.filter((record) => !selectedSet.has(record));
    this.clampCurrentPage();
    this.selection.clear();
    this.allSelected = false;
    this.selectRecord(this.records[0] || null);
    this.publishHeaderActionState('hide', selectedRecords);
  }

  private clearFilterAction(): void {
    this.pendingDialogRequestData = null;
    this.appliedDialogFilterPayload = null;
    this.appliedDialogFilterSummaryLines = [];
    this.hasDialogFilterApplied = false;
    this.loadRecords().subscribe();
    this.publishHeaderActionState('clear_filter');
  }

  protected getSelectedRecordsForAction(): any[] {
    const selectedRecords = Array.from(this.selection);
    if (selectedRecords.length > 0) {
      return selectedRecords;
    }

    return this.selectedRecord ? [this.selectedRecord] : [];
  }

  getCellActionConfig(column: any): any | null {
    const actions = Array.isArray(this.data?.cellActions) ? this.data.cellActions : [];
    if (!actions.length || !column) {
      return null;
    }

    const selectedEntity = this.getCurrentEntityValue();
    if (this.cellActionCacheEntity !== selectedEntity || this.cellActionCacheSource !== actions) {
      this.cellActionCache = new WeakMap<object, any | null>();
      this.cellActionCacheEntity = selectedEntity;
      this.cellActionCacheSource = actions;
    }

    if (typeof column === 'object' && this.cellActionCache.has(column)) {
      return this.cellActionCache.get(column) ?? null;
    }

    const matchedAction = actions.find((action: any) => (
      this.cellActionMatchesEntity(action, selectedEntity) &&
      this.cellActionMatchesColumn(action, column)
    )) || null;

    if (typeof column === 'object') {
      this.cellActionCache.set(column, matchedAction);
    }

    return matchedAction;
  }

  openCellActionDialog(event: MouseEvent, record: any, column: any, action: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectRecord(record);

    if (!this.cellActionDialog || !this.dialog) {
      this.publishHeaderActionState(action?.actionId || 'cell_action', { record, column, action });
      return;
    }

    const cellValue = this.formatCellValue(record?.[column?.key], column);
    const titleValue = this.resolveRecordValueByCandidateKeys(record, action?.titleValueKeys);
    const fallbackId = titleValue ?? record?.quote_id ?? record?.quoteId ?? record?.id ?? record?.uuid ?? cellValue ?? '';
    this.activeCellDialogConfig = action;
    this.activeCellDialogTitle = this.interpolateTemplate(
      action?.dialogTitle || action?.title || 'Edit Transactions',
      { record, column, value: cellValue, fallbackId }
    );
    this.activeCellDialogColumns = Array.isArray(action?.tableColumns) ? this.clonePayload(action.tableColumns) : [];
    this.activeCellDialogRows = this.resolveCellDialogRows(action, record, column);
    this.activeCellDialogPageSize = action?.pageSize || this.activeCellDialogRows.length || 10;
    this.activeCellDialogAnnotation = action?.annotations?.defaultValue || '';

    this.cellActionDialogRef = this.dialog.open(this.cellActionDialog, {
      width: action?.dialogWidth || '78vw',
      maxWidth: action?.dialogMaxWidth || '90vw',
      height: action?.dialogHeight,
      maxHeight: action?.dialogMaxHeight || '90vh',
      panelClass: 'entity-cell-action-dialog-panel'
    });
  }

  closeCellActionDialog(): void {
    this.cellActionDialogRef?.close();
    this.cellActionDialogRef = null;
  }

  saveCellActionDialog(): void {
    this.publishHeaderActionState(this.activeCellDialogConfig?.actionId || 'cell_action', {
      rows: this.activeCellDialogRows,
      annotation: this.activeCellDialogAnnotation,
    });
    this.closeCellActionDialog();
  }

  sendCellActionDialog(): void {
    this.publishHeaderActionState(this.activeCellDialogConfig?.sendActionId || `${this.activeCellDialogConfig?.actionId || 'cell_action'}_send`, {
      rows: this.activeCellDialogRows,
      annotation: this.activeCellDialogAnnotation,
    });
  }

  addCellDialogRow(): void {
    if (!Array.isArray(this.activeCellDialogRows)) {
      this.activeCellDialogRows = [];
    }

    this.activeCellDialogRows.push(this.buildEmptyCellDialogRow());
  }

  removeCellDialogRow(index: number): void {
    if (index < 0 || index >= this.activeCellDialogRows.length) {
      return;
    }

    this.activeCellDialogRows.splice(index, 1);
  }

  private buildEmptyCellDialogRow(): any {
    const fields = Array.isArray(this.activeCellDialogConfig?.detailFields) ? this.activeCellDialogConfig.detailFields : [];
    const fieldsByKey = fields.reduce((acc: any, field: any) => {
      if (field?.key) {
        acc[field.key] = field;
      }
      return acc;
    }, {});

    return (Array.isArray(this.activeCellDialogColumns) ? this.activeCellDialogColumns : []).reduce((row: any, column: any) => {
      const key = column?.key;
      if (!key || column?.type === 'button') {
        return row;
      }

      const field = fieldsByKey[key] || column;
      row[key] = '';
      row[`${key}_control_type`] = column?.controlType || field?.controlType || 'text';
      row[`${key}_options`] = this.resolveCellDialogFieldOptions(field, '');
      return row;
    }, {});
  }

  shouldShowCellActionValue(action: any): boolean {
    return action?.hideCellValue !== true;
  }

  getCellDialogEmptyRows(): number[] {
    const pageSize = Number(this.activeCellDialogPageSize) || 0;
    const rowCount = Array.isArray(this.activeCellDialogRows) ? this.activeCellDialogRows.length : 0;
    const emptyRowCount = Math.max(pageSize - rowCount, 0);
    return Array.from({ length: emptyRowCount }, (_value, index) => index);
  }

  private downloadRecords(action?: any): void {
    const recordsToExport = this.records || [];
    if (recordsToExport.length === 0) {
      this.publishHeaderActionState('download', []);
      return;
    }

    const csv = this.buildCsv(recordsToExport);
    const filename = action?.filename || `${this.data?.widgetId || 'entity-records'}.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
    this.publishHeaderActionState('download', recordsToExport);
  }

  private buildCsv(records: any[]): string {
    const columns = Array.isArray(this.data?.columns) && this.data.columns.length > 0
      ? this.data.columns.map((column: any) => column.key)
      : Object.keys(records[0] || {});
    const header = columns.map((column: string) => this.escapeCsvValue(column)).join(',');
    const rows = records.map((record) =>
      columns.map((column: string) => this.escapeCsvValue(this.formatCellValue(record?.[column]))).join(',')
    );

    return [header, ...rows].join('\r\n');
  }

  private escapeCsvValue(value: any): string {
    const stringValue = value === null || value === undefined ? '' : String(value);
    if (/[",\r\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  }

  private cellActionMatchesEntity(action: any, selectedEntity: any): boolean {
    const allowedEntities = Array.isArray(action?.entities)
      ? action.entities
      : Array.isArray(action?.entityValues)
        ? action.entityValues
        : [];

    if (!allowedEntities.length) {
      return true;
    }

    const normalizedSelected = this.normalizeColumnToken(selectedEntity);
    return allowedEntities
      .map((entity: any) => this.normalizeColumnToken(entity))
      .includes(normalizedSelected);
  }

  private cellActionMatchesColumn(action: any, column: any): boolean {
    const columnKeys = [
      action?.columnKey,
      ...(Array.isArray(action?.columnKeys) ? action.columnKeys : []),
      ...(Array.isArray(action?.columnLabels) ? action.columnLabels : []),
    ].filter((value: any) => value !== null && value !== undefined);

    const candidates = [
      column?.key,
      column?.label,
      this.normalizeColumnToken(column?.key),
      this.normalizeColumnToken(column?.label),
    ].map((value: any) => String(value || '').trim().toLowerCase());

    const normalizedPattern = this.normalizeColumnToken(action?.columnPattern);
    if (normalizedPattern && candidates.some((candidate: string) => (
      this.normalizeColumnToken(candidate).includes(normalizedPattern)
    ))) {
      return true;
    }

    if (!columnKeys.length) {
      return false;
    }

    return columnKeys.some((key: any) => {
      const normalizedKey = String(key || '').trim().toLowerCase();
      return candidates.includes(normalizedKey) ||
        candidates.includes(this.normalizeColumnToken(normalizedKey));
    });
  }

  private normalizeColumnToken(value: any): string {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private getCurrentEntityValue(): any {
    const entityFilterKey = this.getEntityFilterKey();
    const selectedEntity = this.currentFilters?.[entityFilterKey];
    if (selectedEntity !== null && selectedEntity !== undefined && String(selectedEntity).trim()) {
      return selectedEntity;
    }

    const dropdown = this.data?.header?.dropdowns?.find((item: any) => item?.key === entityFilterKey);
    return dropdown?.defaultValue || null;
  }

  private resolveCellDialogRows(action: any, record: any, column?: any): any[] {
    const rowSourceKey = action?.rowsKey || action?.dataKey;
    const rowSource = rowSourceKey ? record?.[rowSourceKey] : null;

    if (action?.rowsSource === 'recordAttributes') {
      return this.buildCellDialogRowsFromRecord(action, record);
    }

    if (action?.rowsSource === 'fieldDetails') {
      if (action?.rowsLayout === 'fieldsAsColumns') {
        return this.buildCellDialogRowsFromFieldColumns(action, record, column);
      }
      return this.buildCellDialogRowsFromFieldDetails(action, record, column);
    }

    const rows = Array.isArray(rowSource)
      ? rowSource
      : Array.isArray(action?.rows)
        ? action.rows
        : [];

    return this.clonePayload(rows);
  }

  private buildCellDialogRowsFromFieldDetails(action: any, record: any, column?: any): any[] {
    const fields = Array.isArray(action?.detailFields) ? action.detailFields : [];
    if (!fields.length) {
      return [];
    }

    const cellValue = column?.key ? record?.[column.key] : null;
    return fields.map((field: any, index: number) => {
      const value = this.resolveDetailFieldValue(field, record, cellValue);
      const displayValue = this.formatCellDialogFieldValue(field, value);
      return {
        serial: index + 1,
        attribute: field?.label || this.formatColumnLabel(field?.key),
        last_updated_on: this.resolveRecordValueByCandidateKeys(record, action?.updatedAtKeys) || '',
        prior_value: displayValue,
        new_value: displayValue,
        value: displayValue,
        action: field?.actionLabel || action?.deleteButtonLabel || 'Delete',
        control_type: field?.controlType || 'text',
        options: this.resolveCellDialogFieldOptions(field, value),
      };
    });
  }

  private buildCellDialogRowsFromFieldColumns(action: any, record: any, column?: any): any[] {
    const fields = Array.isArray(action?.detailFields) ? action.detailFields : [];
    if (!fields.length) {
      return [];
    }

    const cellValue = column?.key ? record?.[column.key] : null;
    const row = fields.reduce((acc: any, field: any) => {
      const fieldKey = field?.key;
      if (!fieldKey) {
        return acc;
      }

      const value = this.resolveDetailFieldValue(field, record, cellValue);
      const displayValue = this.formatCellDialogFieldValue(field, value);
      acc[fieldKey] = displayValue;
      acc[`${fieldKey}_options`] = this.resolveCellDialogFieldOptions(field, value);
      acc[`${fieldKey}_control_type`] = field?.controlType || 'text';
      return acc;
    }, {});

    return [row];
  }

  private formatCellDialogFieldValue(field: any, value: any): string {
    if (field?.controlType === 'date') {
      return this.formatDateOnlyValue(value);
    }

    const options = Array.isArray(field?.options) ? field.options.map((option: any) => String(option).toLowerCase()) : [];
    if (field?.controlType === 'select' && options.includes('yes') && options.includes('no')) {
      const normalized = String(value ?? '').trim().toLowerCase();
      if (['true', '1', 'y', 'yes'].includes(normalized)) {
        return 'Yes';
      }
      if (['false', '0', 'n', 'no'].includes(normalized)) {
        return 'No';
      }
    }

    return this.formatCellValue(value);
  }

  private resolveCellDialogFieldOptions(field: any, value: any): any[] {
    const configuredOptions = Array.isArray(field?.options) ? field.options : [];
    if (configuredOptions.length > 0) {
      return configuredOptions.map((option: any) => {
        if (option && typeof option === 'object') {
          return {
            label: option.label ?? option.display_name ?? option.displayName ?? option.name ?? option.value ?? '',
            value: option.value ?? option.name ?? option.label ?? option.display_name ?? option.displayName ?? '',
          };
        }

        return { label: String(option), value: option };
      });
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    const label = this.formatCellValue(value);
    return [{ label, value: label }];
  }

  private resolveDetailFieldValue(field: any, record: any, cellValue: any): any {
    const candidateKeys = [
      field?.key,
      ...(Array.isArray(field?.keys) ? field.keys : []),
    ].filter((value: any) => value !== null && value !== undefined);

    for (const source of [record, cellValue]) {
      const resolved = this.pickValueByCandidateKeys(source, candidateKeys);
      if (resolved !== undefined) {
        return resolved;
      }
    }

    return '';
  }

  private resolveRecordValueByCandidateKeys(record: any, candidateKeys: any): any {
    const keys = Array.isArray(candidateKeys) ? candidateKeys : [];
    return this.pickValueByCandidateKeys(record, keys);
  }

  private pickValueByCandidateKeys(source: any, candidateKeys: any[]): any {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return undefined;
    }

    for (const key of candidateKeys) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        return source[key];
      }
    }

    const entries = Object.entries(source);
    for (const key of candidateKeys) {
      const normalizedKey = this.normalizeColumnToken(key);
      const match = entries.find(([sourceKey]) => this.normalizeColumnToken(sourceKey) === normalizedKey);
      if (match) {
        return match[1];
      }
    }

    return undefined;
  }

  private buildCellDialogRowsFromRecord(action: any, record: any): any[] {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      return [];
    }

    const excludedFields = new Set(
      (Array.isArray(action?.excludeFields) ? action.excludeFields : [])
        .map((field: any) => this.normalizeColumnToken(field))
    );
    const updatedAtKeys = Array.isArray(action?.updatedAtKeys) && action.updatedAtKeys.length
      ? action.updatedAtKeys
      : ['last_updated_on', 'updated_date', 'updated_at', 'lastUpdatedOn', 'lastUpdatedAt'];
    const updatedAtEntry = Object.entries(record).find(([key]) => (
      updatedAtKeys.some((candidate: any) => this.normalizeColumnToken(candidate) === this.normalizeColumnToken(key))
    ));
    const lastUpdatedOn = updatedAtEntry ? this.formatCellValue(updatedAtEntry[1]) : '';

    return Object.entries(record)
      .filter(([key]) => !excludedFields.has(this.normalizeColumnToken(key)))
      .map(([key, value], index) => {
        const displayValue = this.formatCellValue(value, { key });
        return {
          serial: index + 1,
          attribute: this.formatColumnLabel(key),
          last_updated_on: lastUpdatedOn,
          prior_value: displayValue,
          new_value: displayValue,
          action: action?.deleteButtonLabel || 'Delete',
          source_key: key,
        };
      });
  }

  private interpolateTemplate(template: string, context: any): string {
    return String(template || '').replace(/\{\{\s*([^}]+)\s*\}\}/g, (_match, expression) => {
      const path = String(expression || '').trim().split('.');
      let value = context;
      for (const part of path) {
        value = value?.[part];
      }

      return value === null || value === undefined || value === '' ? String(context?.fallbackId || '') : String(value);
    });
  }

  private publishHeaderActionState(actionId: string, payload?: any): void {
    if (!this.data?.widgetId) {
      return;
    }

    this.globalFilterService.updateFilterSelection(
      `${this.data.widgetId}_${actionId}`,
      [payload ?? actionId],
      false,
      this.data.widgetId
    );
  }

  openFilterDialog(action?: any): void {
    const entityFilterKey = this.getEntityFilterKey();
    const entityDropdown = this.data?.header?.dropdowns?.find((dropdown: any) => dropdown?.key === entityFilterKey);
    const entityOptionsConfig = entityDropdown?.options || {};
    const filterAction = action || this.data?.header?.actions?.find((action: any) => action?.actionId === 'filter') || {};
    const filterFunctionName =
      filterAction?.attributeFunctionName ||
      filterAction?.functionName ||
      'GenericEntityAttributesService';
    const domainFunctionName =
      filterAction?.attributeValuesFunctionName ||
      filterAction?.domainFunctionName ||
      filterAction?.domainAttributeFunctionName ||
      filterAction?.funtionName ||
      'GenericAttributeValuesService';
    const selectedEntity = this.currentFilters[entityFilterKey];
    const selectedFilters = this.getRequestFilters();
    const attributeApiUrl = filterAction?.attributeapiUrl || filterAction?.attributeApiUrl || filterAction?.apiUrl;
    const domainApiUrl = filterAction?.domainapiurl || filterAction?.domainApiUrl || filterAction?.domainApiURL;

    const filterPayload = this.applyFunctionNameToPayload(
      this.clonePayload(
        (attributeApiUrl ? this.data?.customPayload : entityOptionsConfig?.customPayload) ||
        this.data?.customPayload ||
        this.data?.filterPayload ||
        entityOptionsConfig?.customPayload
      ),
      filterFunctionName,
    );
    const attributePayload = this.buildAttributePayload(filterPayload, selectedEntity);
    const filterEntity =
      attributePayload?.requestData?.entity ||
      attributePayload?.entity ||
      selectedEntity;

    // Build domain payload template (will be used for each domain attribute)
    const domainPayloadTemplate = {
      requestData: {},
      responseList: [],
      entity: filterAction?.entity || this.data?.entity || '',
      workflowId: filterAction?.workflowId || this.data?.workflowId || '',
      endpoints: [
        {
          functionName: domainFunctionName,
          version: filterAction?.version || '1.0'
        }
      ],
      communicationType: 'rest',
      version: this.data?.version || '1.0',
      functionName: domainFunctionName
    };

    const dialogRef = this.dialog.open(SpricedFilterDialogComponent, {
      width: '60vw',
      maxWidth: '70vw',
      panelClass: 'spriced-filter-dialog-panel',
      data: {
        filterConfigUrl: this.data.filterConfigUrl,
        filterApiUrl: attributeApiUrl || entityOptionsConfig?.apiUrl || this.data.filterConfigUrl || this.data.apiUrl,
        filterMethod: filterAction?.method || entityOptionsConfig?.method || 'post',
        filterHeaders: filterAction?.headers || entityOptionsConfig?.headers || this.data.headers,
        domainApiUrl: domainApiUrl,
        domainPayloadTemplate: domainPayloadTemplate,
        defaultApplyPayload: filterAction?.defaultApplyPayload || this.data?.defaultApplyPayload || null,
        submitFunctionName: filterAction?.submitFunctionName || 'GenericDynamicReadServices',
        numericFilterAttributes:
          filterAction?.numericFilterAttributes ||
          this.data?.numericFilterAttributes ||
          [],
        domainFunctionName,
        selectedEntity,
        filterEntity,
        domainAttributeValueKeyByEntity:
          filterAction?.domainAttributeValueKeyByEntity ||
          filterAction?.domainAttributeValueKeysByEntity ||
          this.data?.domainAttributeValueKeyByEntity ||
          this.data?.domainAttributeValueKeysByEntity,
        filterPayload: attributePayload,
        appliedFilterPayload: this.appliedDialogFilterPayload,
        selectedFilters,
        tableColumns: this.data?.columns || []
      }
    });

    dialogRef.afterClosed().subscribe((result) => this.handleFilterDialogResult(result));
  }

  /** Cancel only dismisses the filter window; it must not alter or reload its parent. */
  private handleFilterDialogResult(result: any): void {
    if (!result) {
      return;
    }

    this.pendingDialogRequestData = result;
    this.appliedDialogFilterPayload = this.clonePayload(result);
    this.appliedDialogFilterSummaryLines = Array.isArray(result?.__appliedFilterSummaryLines)
      ? [...result.__appliedFilterSummaryLines]
      : [];
    this.hasDialogFilterApplied = true;
    this.loadRecords().subscribe();
  }

  protected openSavedFiltersDialog(action?: any): void {
    const dialogRef = this.dialog.open(SpricedSavedFiltersDialogComponent, {
      width: action?.dialogWidth || '60vw',
      maxWidth: action?.dialogMaxWidth || '70vw',
      panelClass: 'spriced-saved-filters-dialog-panel',
      data: {
        action,
        savedFilters: action?.savedFilters || action?.filters || [],
        currentFilters: this.getRequestFilters(),
        selectedRecord: this.selectedRecord,
        records: this.records,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.publishHeaderActionState('menu', result);
    });
  }

  toggleAllSelection() {
    this.allSelected = !this.allSelected;
    if (this.allSelected) {
      this.records.forEach(record => this.selection.add(record));
    } else {
      this.selection.clear();
    }
  }

  toggleSelection(record: any) {
    if (this.selection.has(record)) {
      this.selection.delete(record);
    } else {
      this.selection.add(record);
    }
    this.allSelected = this.selection.size === this.records.length;
  }

  selectRecord(record: any) {
    this.selectedRecord = record;
    this.recordSelected.emit(record);
    if (this.data?.widgetId) {
      // Pass an array with the record, or an empty array if the record is null
      this.globalFilterService.updateFilterSelection(this.data.widgetId, record ? [record] : [], false, this.data.widgetId);
    }
  }

  onDropdownSelectionChange(dropdownKey: string, selectedValue: any): void {
    const dropdown = this.data?.header?.dropdowns?.find((item: any) => item.key === dropdownKey) || { key: dropdownKey };
    const normalizedValue = this.normalizeDropdownSelection(dropdown, this.getDropdownOptionValue(dropdown, selectedValue));

    this.currentFilters[dropdownKey] = normalizedValue;
    this.syncDropdownSelectionFromOptions(dropdownKey);
    this.data.columns = undefined; // Clear existing columns to force regeneration
    this.loadRecords().subscribe();

    if (this.data?.widgetId) {
      const isQueryParam = dropdown?.isQueryParam === true || this.data?.method?.toLowerCase() === 'post';
      this.globalFilterService.updateFilterSelection(dropdownKey, [normalizedValue], isQueryParam, this.data.widgetId);
    }
  }

  private clonePayload(payload: any): any {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    const clonedPayload = JSON.parse(JSON.stringify(payload));
    const appliedFormState = payload?.__appliedFilterFormState;
    if (appliedFormState && typeof appliedFormState === 'object') {
      Object.defineProperty(clonedPayload, '__appliedFilterFormState', {
        value: JSON.parse(JSON.stringify(appliedFormState)),
        enumerable: false,
      });
    }

    return clonedPayload;
  }

  private applyFunctionNameToPayload(payload: any, functionName: string): any {
    const clonedPayload = this.clonePayload(payload);
    if (!clonedPayload || typeof clonedPayload !== 'object' || !functionName) {
      return clonedPayload;
    }

    clonedPayload.functionName = functionName;

    if (Array.isArray(clonedPayload.endpoints) && clonedPayload.endpoints.length > 0) {
      clonedPayload.endpoints = clonedPayload.endpoints.map((endpoint: any, index: number) => {
        if (index !== 0 || !endpoint || typeof endpoint !== 'object') {
          return endpoint;
        }

        return {
          ...endpoint,
          functionName,
        };
      });
    } else {
      clonedPayload.endpoints = [
        {
          functionName,
          version: this.data?.version || '1.0',
        },
      ];
    }

    return clonedPayload;
  }

  private buildAttributePayload(payload: any, selectedEntity: any): any {
    const attributePayload = payload && typeof payload === 'object' ? payload : {};
    attributePayload.requestData = {
      ...(attributePayload.requestData || {}),
      entity: selectedEntity || attributePayload.requestData?.entity || attributePayload.entity
    };
    return attributePayload;
  }

  isFilterActionApplied(action: any): boolean {
    return action?.actionId === 'filter' && this.hasDialogFilterApplied;
  }

  getAppliedFilterSummaryLines(): string[] {
    if (this.appliedDialogFilterSummaryLines.length > 0) {
      return this.appliedDialogFilterSummaryLines;
    }

    const filters = this.getAppliedDialogUserFilters();
    if (!filters.length) {
      return [];
    }

    return filters.map((filter: any, index: number) => {
      const fieldLabel = this.getAppliedFilterFieldLabel(filter?.attributeName);
      const operatorLabel = this.getAppliedFilterOperatorLabel(filter?.operator);
      const valueLabel = this.formatAppliedFilterValue(filter?.attributeValue);
      const beginScope = String(filter?.beginScope || '');
      const endScope = String(filter?.endScope || '');
      const logicalOperator = this.getAppliedFilterLogicalOperator(filter, index, filters.length);
      return `${beginScope}${fieldLabel} ${operatorLabel} ${valueLabel}${endScope}${logicalOperator}`.trim();
    });
  }

  private getAppliedFilterLogicalOperator(filter: any, index: number, totalFilters: number): string {
    if (index >= totalFilters - 1) {
      return '';
    }

    const logicalOperator = String(filter?.logicalOperator || 'and').trim().toLowerCase();
    return logicalOperator ? ` ${logicalOperator}` : ' and';
  }

  private getAppliedDialogUserFilters(): any[] {
    const filters = this.appliedDialogFilterPayload?.requestData?.filters;
    if (!Array.isArray(filters)) {
      return [];
    }

    const defaults = this.data?.defaultApplyPayload?.requestData?.filters;
    const defaultFilters = Array.isArray(defaults) ? defaults : [];
    return filters.filter((filter: any) => {
      if (this.isSystemAppliedFilter(filter)) {
        return false;
      }

      return !defaultFilters.some((defaultFilter: any) =>
        JSON.stringify(defaultFilter || {}) === JSON.stringify(filter || {})
      );
    });
  }

  private isSystemAppliedFilter(filter: any): boolean {
    const normalizedAttribute = String(filter?.attributeName || filter?.key || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s-]+/g, '');
    return ['offset', 'limit', 'pagesize', 'page', 'currentpage', 'orderby', 'order', 'sort', 'sortby'].includes(normalizedAttribute);
  }

  private getAppliedFilterFieldLabel(attributeName: any): string {
    const resolvedAttributeName = String(attributeName || '').trim();
    const column = Array.isArray(this.data?.columns)
      ? this.data.columns.find((item: any) => item?.key === resolvedAttributeName || item?.label === resolvedAttributeName)
      : null;
    return column?.label || this.formatColumnLabel(resolvedAttributeName);
  }

  private getAppliedFilterOperatorLabel(operator: any): string {
    const normalized = String(operator || '').trim();
    const labels: Record<string, string> = {
      is_equal_to: 'Is Equal To',
      is_not_equal_to: 'Is Not Equal To',
      in: 'In',
      not_in: 'Not In',
      contains: 'Contains',
      greater_than: 'Greater Than',
      less_than: 'Less Than',
      is_Null: 'Is Null',
      is_not_Null: 'Is Not Null',
    };
    return labels[normalized] || this.formatColumnLabel(normalized);
  }

  private formatAppliedFilterValue(value: any): string {
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }
}

